import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getWriteoffVendors,
    getWriteoffPOs,
    getWriteoffInvoices,
    getWriteoffInvoiceTaxes,
    saveVendorPayableWriteoff,
} from '../../api/PurchaseAPI/vendorPayableWriteoffAPI';

// API errors arrive as the response body ({ ErrorDetails: { ErrorMessage } }), a string, or an Error.
const errMsg = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || (typeof err === 'string' ? err : fallback);

// ── Thunks ─────────────────────────────────────────────────────────────────────

export const fetchWriteoffVendors = createAsyncThunk(
    'vendorPayableWriteoff/fetchVendors',
    async (params, { rejectWithValue }) => {
        try { return await getWriteoffVendors(params); }
        catch (err) { return rejectWithValue(errMsg(err, 'Failed to fetch vendors')); }
    }
);

export const fetchWriteoffPOs = createAsyncThunk(
    'vendorPayableWriteoff/fetchPOs',
    async (params, { rejectWithValue }) => {
        try { return await getWriteoffPOs(params); }
        catch (err) { return rejectWithValue(errMsg(err, 'Failed to fetch POs')); }
    }
);

export const fetchWriteoffInvoices = createAsyncThunk(
    'vendorPayableWriteoff/fetchInvoices',
    async (params, { rejectWithValue }) => {
        try { return await getWriteoffInvoices(params); }
        catch (err) { return rejectWithValue(errMsg(err, 'Failed to fetch invoices')); }
    }
);

export const fetchWriteoffInvoiceTaxes = createAsyncThunk(
    'vendorPayableWriteoff/fetchInvoiceTaxes',
    async (params, { rejectWithValue }) => {
        try { return await getWriteoffInvoiceTaxes(params); }
        catch (err) { return rejectWithValue(errMsg(err, 'Failed to fetch invoice taxes')); }
    }
);

export const submitVendorPayableWriteoff = createAsyncThunk(
    'vendorPayableWriteoff/submit',
    async (payload, { rejectWithValue }) => {
        try { return await saveVendorPayableWriteoff(payload); }
        catch (err) { return rejectWithValue(errMsg(err, 'Failed to save vendor payable write-off')); }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    vendors:  [],   // [{ VendorCode, VendorName }]
    pos:      [],   // [{ PONo, PONoDesc }]
    invoices: [],   // [{ InvoiceNo, InvoiceDate, PONo, CCCode, BasicValue, InvoiceBalance, RetentionBalance, HoldBalance, TotalBalance, TaxAmt }]
    invoicesLoaded: false,
    taxes:    [],   // tax / deduction lines of the invoice in the popup

    loading: { vendors: false, pos: false, invoices: false, taxes: false, save: false },
    errors:  { vendors: null,  pos: null,  invoices: null,  taxes: null },

    saveStatus: null,   // null | 'success' | 'failed'
    saveRefNo:  null,
    saveError:  null,
};

const vendorPayableWriteoffSlice = createSlice({
    name: 'vendorPayableWriteoff',
    initialState,
    reducers: {
        clearWriteoffVendors:  (s) => { s.vendors = []; s.errors.vendors = null; },
        clearWriteoffPOs:      (s) => { s.pos = []; s.errors.pos = null; },
        clearWriteoffInvoices: (s) => { s.invoices = []; s.invoicesLoaded = false; s.errors.invoices = null; },
        clearWriteoffTaxes:    (s) => { s.taxes = []; s.errors.taxes = null; },
        clearWriteoffSaveResult: (s) => { s.saveStatus = null; s.saveRefNo = null; s.saveError = null; },
        resetVendorPayableWriteoff: () => initialState,
    },
    extraReducers: (builder) => {
        const list = (thunk, key, after) => builder
            .addCase(thunk.pending,   (s) => { s.loading[key] = true; s.errors[key] = null; s[key] = []; })
            .addCase(thunk.fulfilled, (s, a) => { s.loading[key] = false; s[key] = a.payload?.Data || []; if (after) after(s); })
            .addCase(thunk.rejected,  (s, a) => { s.loading[key] = false; s.errors[key] = a.payload; });

        list(fetchWriteoffVendors, 'vendors');
        list(fetchWriteoffPOs, 'pos');
        list(fetchWriteoffInvoices, 'invoices', (s) => { s.invoicesLoaded = true; });
        list(fetchWriteoffInvoiceTaxes, 'taxes');

        builder
            .addCase(submitVendorPayableWriteoff.pending, (s) => {
                s.loading.save = true; s.saveStatus = null; s.saveRefNo = null; s.saveError = null;
            })
            .addCase(submitVendorPayableWriteoff.fulfilled, (s, a) => {
                s.loading.save = false;
                // Success literal is "Submited$<RefNo>" (spSaveWriteoffPOInvoicePayment spelling)
                const raw = String(a.payload?.Data ?? '');
                const [msg, refNo] = raw.split('$');
                if (msg.trim() === 'Submited') {
                    s.saveStatus = 'success';
                    s.saveRefNo = (refNo || '').trim();
                } else {
                    s.saveStatus = 'failed';
                    s.saveError = raw || 'Save failed';
                }
            })
            .addCase(submitVendorPayableWriteoff.rejected, (s, a) => {
                s.loading.save = false; s.saveStatus = 'failed'; s.saveError = a.payload;
            });
    },
});

export const {
    clearWriteoffVendors, clearWriteoffPOs, clearWriteoffInvoices, clearWriteoffTaxes,
    clearWriteoffSaveResult, resetVendorPayableWriteoff,
} = vendorPayableWriteoffSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────

const st = (s) => s.vendorPayableWriteoffCreation;
export const selectWOVendors        = (s) => st(s).vendors;
export const selectWOPOs            = (s) => st(s).pos;
export const selectWOInvoices       = (s) => st(s).invoices;
export const selectWOInvoicesLoaded = (s) => st(s).invoicesLoaded;
export const selectWOTaxes          = (s) => st(s).taxes;
export const selectWOLoading        = (s) => st(s).loading;
export const selectWOErrors         = (s) => st(s).errors;
export const selectWOSaveStatus     = (s) => st(s).saveStatus;
export const selectWOSaveRefNo      = (s) => st(s).saveRefNo;
export const selectWOSaveError      = (s) => st(s).saveError;

export default vendorPayableWriteoffSlice.reducer;
